import { useEffect, useMemo, useRef, useState } from "react";
import {
  getAccessScopeService,
  searchGlobalService,
} from "api/services/globalSearchServices";
import { getObligationGroupsStatusCounts } from "api/services/obligationsServices";

const MIN_CHARS = 3;
const PAGE_SIZE = 20;

const RECENTS_KEY = "global_search_recently_viewed_v1";

let ACCESS_SCOPE_CACHE = null;
let ACCESS_SCOPE_PROMISE = null;

export default function GlobalSearchBarFunctionalitues() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [helpText, setHelpText] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [loading, setLoading] = useState(false);
  const [groupStatusMap, setGroupStatusMap] = useState({});
  const [visibleCountObligations, setVisibleCountObligations] = useState(PAGE_SIZE);
  const [visibleCountFiles, setVisibleCountFiles] = useState(PAGE_SIZE);
  const [visibleCountGlossary, setVisibleCountGlossary] = useState(PAGE_SIZE);
  const [submittedQuery, setSubmittedQuery] = useState("");

  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try {
      if (typeof window === "undefined") return [];
      const raw = window.localStorage.getItem(RECENTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [accessScopeUsers, setAccessScopeUsers] = useState([]);
  const [accessScopeFrameworks, setAccessScopeFrameworks] = useState([]);

  const [selectedFrameworkKeys, setSelectedFrameworkKeys] = useState([]);
  const [selectedResponsibleKeys, setSelectedResponsibleKeys] = useState([]);

  const [serverResults, setServerResults] = useState([]);

  const wrapperRef = useRef(null);
  const panelRef = useRef(null);

  const isMac = useMemo(() => {
    const platform =
      navigator.userAgentData?.platform || navigator.platform || navigator.userAgent;
    return /mac/i.test(platform);
  }, []);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(RECENTS_KEY, JSON.stringify(recentlyViewed || []));
    } catch {
    }
  }, [recentlyViewed]);

  const loadAccessScope = async () => {
    try {
      if (ACCESS_SCOPE_CACHE) {
        setAccessScopeUsers(ACCESS_SCOPE_CACHE.users || []);
        setAccessScopeFrameworks(ACCESS_SCOPE_CACHE.frameworks || []);
        return;
      }

      if (!ACCESS_SCOPE_PROMISE) {
        ACCESS_SCOPE_PROMISE = getAccessScopeService();
      }

      const data = await ACCESS_SCOPE_PROMISE;
      ACCESS_SCOPE_CACHE = data;

      setAccessScopeUsers((data && data.users) || []);
      setAccessScopeFrameworks((data && data.frameworks) || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (accessScopeFrameworks.length && selectedFrameworkKeys.length === 0) {
      setSelectedFrameworkKeys(accessScopeFrameworks.map((fw) => fw.id || fw.name));
    }
  }, [accessScopeFrameworks, selectedFrameworkKeys.length]);

  useEffect(() => {
    if (accessScopeUsers.length && selectedResponsibleKeys.length === 0) {
      setSelectedResponsibleKeys(accessScopeUsers.map((u) => u.id || u.name));
    }
  }, [accessScopeUsers, selectedResponsibleKeys.length]);

  const handleOpen = () => {
    setOpen(true);
    loadAccessScope();
  };

  const handleClose = () => setOpen(false);

  const getFrameworkCode = (name) =>
    (String(name || "").match(/[A-Za-z0-9]+/g) || [])
      .slice(0, 3)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "FW";

  const addToRecentlyViewed = (item) => {
    const keyOf = (x) => `${x?.type || ""}:${x?.id || ""}`;
    setRecentlyViewed((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const withoutDup = list.filter((x) => keyOf(x) !== keyOf(item));
      return [item, ...withoutDup].slice(0, 6);
    });
  };

  const toggleFrameworkKey = (key) => {
    setSelectedFrameworkKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const toggleResponsibleKey = (key) => {
    setSelectedResponsibleKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const showResults =
    submittedQuery.trim().length >= MIN_CHARS &&
    value.trim().length >= MIN_CHARS &&
    value.trim() === submittedQuery.trim();

  const labelForFooter = (key, n) => {
    if (key === "Obligations") return n === 1 ? "Obligation" : "Obligations";
    if (key === "Files") return n === 1 ? "File" : "Files";
    if (key === "Glossary") return n === 1 ? "Glossary" : "Glossary";
    return key;
  };

  const formatYMD = (iso) => {
    if (!iso) return "";
    const s = String(iso);
    const ymd = s.slice(0, 10);
    return ymd.includes("-") ? ymd.replaceAll("-", "/") : ymd;
  };

  const normalizeFromApi = (data) => {
    const out = [];

    if (Array.isArray(data?.hits)) {
      for (const h of data.hits) {
        const mainResp = h?.group?.main_responsible;
        const groupId = h?.group?.group_id || h?.group_id || h?.id;
        const recurrenceId = h?.obligation_id || h?.obligation?.id;

        out.push({
          id: groupId || recurrenceId,
          group_id: groupId,
          obligation_id: recurrenceId,

          type: "Obligations",
          title: h?.group?.obl_name || "Obligation",
          sub: "",
          obl_tag: h?.group?.obl_tag || "",
          responsible_name: mainResp?.name || h?.obligation?.responsible_name || "",
          responsible_id: mainResp?.id || "",
          status: h?.obligation?.status || "",
          due_date: formatYMD(h?.obligation?.due_date),
          framework_name: h?.framework_snapshot?.framework_name || "",
          framework_filter_type: h?.framework_snapshot?.filter_type || "",
          framework_bg: h?.framework_snapshot?.background_color || "#959595ff",
          framework_font: h?.framework_snapshot?.font_color || "",
        });
      }
      return out;
    }

    if (Array.isArray(data?.obligations)) {
      for (const o of data.obligations) {
        const groupId = o?.group_id || o?.id;
        const recurrenceId = o?.obligation_id;

        out.push({
          id: groupId || recurrenceId,
          group_id: groupId,
          obligation_id: recurrenceId,
          type: "Obligations",
          title: o?.title || o?.obl_name || "Obligation",
          sub: "",
          obl_tag: o?.obl_tag || "",
          status: o?.status || "",
          due_date: formatYMD(o?.due_date),
          responsible_name: o?.responsible_name || "",
          badge_bg: o?.background_color || "#f5f5f5",
          framework_name: o?.framework_name || "",
          framework_filter_type: o?.filter_type || "",
          framework_bg: o?.background_color || "#959595ff",
          framework_font: o?.font_color || "",
        });
      }
    }

    if (Array.isArray(data?.files)) {
      for (const f of data.files) {
        out.push({
          id: f?.id || f?.file_id,
          type: "Files",
          title: f?.title || f?.file_name || "File",
          sub:
            [
              f?.status ? String(f.status) : "",
              f?.uploaded_at ? formatYMD(f.uploaded_at) : "",
            ]
              .filter(Boolean)
              .join(" • ") || "",
        });
      }
    }

    if (Array.isArray(data?.glossary)) {
      for (const g of data.glossary) {
        out.push({
          id: g?.id || g?.term || Math.random().toString(36).slice(2),
          type: "Glossary",
          title: g?.term || "Glossary",
          sub: g?.definition || g?.document?.title || "",
        });
      }
    }

    return out;
  };

  const isObjectId = (v) => /^[a-fA-F0-9]{24}$/.test(String(v || ""));

  const runSearch = async (q, { showMinCharsHelp = true } = {}) => {
    const term = (q || "").trim();

    setOpen(true);

    if (term.length < MIN_CHARS) {
      setLoading(false);
      setVisibleCountObligations(PAGE_SIZE);
      setVisibleCountFiles(PAGE_SIZE);
      setVisibleCountGlossary(PAGE_SIZE);
      setServerResults([]);
      setSubmittedQuery("");

      if (showMinCharsHelp) {
        setHelpText(`Type at least ${MIN_CHARS} characters to search.`);
      }
      return;
    }

    setHelpText("");
    setSubmittedQuery(term);

    setVisibleCountObligations(PAGE_SIZE);
    setVisibleCountFiles(PAGE_SIZE);
    setVisibleCountGlossary(PAGE_SIZE);

    try {
      setLoading(true);
      setServerResults([]);
      const payload = {
        q: term,
        frameworks: (selectedFrameworkKeys || []).filter(isObjectId),
        responsibles: (selectedResponsibleKeys || []).filter(isObjectId),
        skip: 0,
        limit: 50,
      };

      const data = await searchGlobalService(payload);
      const normalized = normalizeFromApi(data);
      setServerResults(normalized);

      const groupIds = Array.from(
        new Set((data?.hits || []).map((h) => h?.group?.group_id).filter(Boolean))
      );

      if (groupIds.length > 0) {
        try {
          const statusRes = await getObligationGroupsStatusCounts({
            group_ids: groupIds,
          });

          const map = {};
          (statusRes?.groups || []).forEach((g) => {
            map[g.group_id] = g;
          });

          setGroupStatusMap(map);
        } catch (err) {
          console.error("Failed to fetch group status counts", err);
        }
      } else {
        setGroupStatusMap({});
      }
    } catch (err) {
      console.error(err);
      setServerResults([]);
      setGroupStatusMap({});
      setHelpText("Could not fetch search results. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);

    setVisibleCountObligations(PAGE_SIZE);
    setVisibleCountFiles(PAGE_SIZE);
    setVisibleCountGlossary(PAGE_SIZE);

    const term = next.trim();

    if (term.length < MIN_CHARS) {
      setLoading(false);
      setHelpText("");
      setServerResults([]);
      setSubmittedQuery("");
      setGroupStatusMap({});
      return;
    }

    setHelpText("");
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    runSearch(value, { showMinCharsHelp: true });
  };

  const handlePressEnter = (e) => {
    e.preventDefault();
    runSearch(value, { showMinCharsHelp: true });
  };

  const searchPoolAllTypes = useMemo(() => {
    if (!showResults) return [];
    return serverResults;
  }, [showResults, serverResults]);

  const footerText = useMemo(() => {
    if (!showResults) return "";
    if (searchPoolAllTypes.length === 0) return "";

    const counts = { Obligations: 0, Files: 0, Glossary: 0 };
    for (const item of searchPoolAllTypes) {
      if (item.type === "Obligations") counts.Obligations++;
      if (item.type === "Files") counts.Files++;
      if (item.type === "Glossary") counts.Glossary++;
    }

    const parts = ["Obligations", "Files", "Glossary"]
      .filter((k) => counts[k] > 0)
      .map((k) => `${counts[k]} ${labelForFooter(k, counts[k])}`);

    return parts.join(", ");
  }, [showResults, searchPoolAllTypes]);

  const obligationsList = useMemo(() => {
    if (!showResults) return [];
    if (selectedType !== "All" && selectedType !== "Obligations") return [];
    return searchPoolAllTypes.filter((x) => x.type === "Obligations");
  }, [showResults, selectedType, searchPoolAllTypes]);

  const filesList = useMemo(() => {
    if (!showResults) return [];
    if (selectedType !== "All" && selectedType !== "Files") return [];
    return searchPoolAllTypes.filter((x) => x.type === "Files");
  }, [showResults, selectedType, searchPoolAllTypes]);

  const glossaryList = useMemo(() => {
    if (!showResults) return [];
    if (selectedType !== "All" && selectedType !== "Glossary") return [];
    return searchPoolAllTypes.filter((x) => x.type === "Glossary");
  }, [showResults, selectedType, searchPoolAllTypes]);

  const visibleObligations = useMemo(
    () => obligationsList.slice(0, visibleCountObligations),
    [obligationsList, visibleCountObligations]
  );
  const visibleFiles = useMemo(
    () => filesList.slice(0, visibleCountFiles),
    [filesList, visibleCountFiles]
  );
  const visibleGlossary = useMemo(
    () => glossaryList.slice(0, visibleCountGlossary),
    [glossaryList, visibleCountGlossary]
  );

  const hasMoreObligations =
    showResults && obligationsList.length > visibleCountObligations;
  const hasMoreFiles = showResults && filesList.length > visibleCountFiles;
  const hasMoreGlossary =
    showResults && glossaryList.length > visibleCountGlossary;

  const filteredRecents = useMemo(() => {
    let list = recentlyViewed;
    if (selectedType !== "All") list = list.filter((x) => x.type === selectedType);
    return list.slice(0, 6);
  }, [recentlyViewed, selectedType]);

  useEffect(() => {
    if (open) loadAccessScope();
  }, [open]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const rawKey = e?.key;
      const key = typeof rawKey === "string" ? rawKey.toLowerCase() : "";
      const shift = !!e?.shiftKey;

      const macCombo = isMac && !!e?.metaKey && shift && key === "k";
      const winCombo = !isMac && !!e?.ctrlKey && shift && key === "k";

      if (macCombo || winCombo) {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => {
          document.getElementById("global-search-input")?.focus();
        }, 0);
        return;
      }

      if (key === "escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMac]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const wrapper = wrapperRef.current;
      const target = e.target;
      const inAntDrawer =
        target?.closest?.(".ant-drawer") ||
        target?.closest?.(".ant-drawer-mask") ||
        target?.closest?.(".ant-drawer-wrap") ||
        target?.closest?.(".ant-drawer-content-wrapper");

      if (inAntDrawer) return;

      if (wrapper && wrapper.contains(target)) return;

      setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [open]);

  return {
    MIN_CHARS,
    PAGE_SIZE,
    loading,

    open,
    value,
    helpText,
    selectedType,

    wrapperRef,
    panelRef,

    isMac,
    showResults,
    footerText,

    obligationsList,
    filesList,
    glossaryList,

    visibleObligations,
    visibleFiles,
    visibleGlossary,

    hasMoreObligations,
    hasMoreFiles,
    hasMoreGlossary,

    visibleCountObligations,
    visibleCountFiles,
    visibleCountGlossary,

    filteredRecents,

    handleOpen,
    handleClose,
    handleChange,
    handlePressEnter,
    handleSearchClick,

    setSelectedType,
    setVisibleCountObligations,
    setVisibleCountFiles,
    setVisibleCountGlossary,

    addToRecentlyViewed,
    getFrameworkCode,

    accessScopeUsers,
    accessScopeFrameworks,

    selectedFrameworkKeys,
    selectedResponsibleKeys,
    groupStatusMap,
    toggleFrameworkKey,
    toggleResponsibleKey,
  };
}